import { useMemo } from "react";
import { AlertCircle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import type { ContentType } from "@/types/ab-tests";
import { getTestableFields } from "@/utils/ab-test-utils";

interface FieldSelectionStepProps {
  selectedFields: ContentType[];
  onFieldToggle: (field: ContentType) => void;
  /** Product bullet points — sets the slot count, max(5, n). */
  bulletPoints?: string[];
}

export function FieldSelectionStep({
  selectedFields,
  onFieldToggle,
  bulletPoints = [],
}: FieldSelectionStepProps) {
  const availableFields = useMemo(() => getTestableFields(bulletPoints.length), [bulletPoints.length]);

  return (
    <div className="max-w-3xl">
      <div className="space-y-5 py-2">
        <div className="space-y-3">
          <div className="space-y-1">
            <h4 className="text-lg font-semibold text-foreground">Choose which fields to test</h4>
            <p className="text-sm text-muted-foreground">
              We recommend selecting just one field per test — isolating a single change produces the most valuable results.
            </p>
          </div>
          <div className="flex flex-col gap-2.5">
            {availableFields.map((field) => (
              <div key={field.value} className="flex items-center gap-2">
                <Checkbox
                  id={`field-${field.value}`}
                  checked={selectedFields.includes(field.value)}
                  onCheckedChange={() => onFieldToggle(field.value)}
                />
                <label htmlFor={`field-${field.value}`} className="text-sm cursor-pointer select-none">
                  {field.label}
                </label>
              </div>
            ))}
          </div>
          {selectedFields.length > 1 && (
            <div className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>Testing multiple fields at once reduces result specificity — it becomes harder to isolate which change drove the outcome.</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
